import {
  IDKitErrorCodes,
  type IDKitResultSession,
} from "@worldcoin/idkit-core";
import { IDKit } from "../idkit";
import type {
  IDKitSessionHookConfig,
  UseIDKitSessionHookResult,
} from "../types";
import { useIDKitFlow } from "./useIDKitFlow";

const SESSION_ID_PATTERN = /^session_[0-9a-fA-F]{128}$/;

function assertSessionId(
  sessionId: string | undefined,
): `session_${string}` | undefined {
  if (sessionId === undefined) {
    return undefined;
  }

  if (sessionId.trim().length === 0 || !SESSION_ID_PATTERN.test(sessionId)) {
    throw IDKitErrorCodes.MalformedRequest;
  }

  return sessionId as `session_${string}`;
}

export function useIDKitSession(
  config: IDKitSessionHookConfig,
): UseIDKitSessionHookResult {
  return useIDKitFlow<IDKitResultSession>(() => {
    const existingSessionId = assertSessionId(config.existing_session_id);
    const builder = existingSessionId
      ? IDKit.proveSession(existingSessionId, {
          app_id: config.app_id,
          rp_context: config.rp_context,
          action_description: config.action_description,
          bridge_url: config.bridge_url,
          require_user_presence: config.require_user_presence ?? false,
          override_connect_base_url: config.override_connect_base_url,
          return_to: config.return_to,
          environment: config.environment,
        })
      : IDKit.createSession({
          app_id: config.app_id,
          rp_context: config.rp_context,
          action_description: config.action_description,
          bridge_url: config.bridge_url,
          require_user_presence: config.require_user_presence ?? false,
          override_connect_base_url: config.override_connect_base_url,
          return_to: config.return_to,
          environment: config.environment,
        });
    return builder.constraints(config.constraints);
  }, config);
}
